import './OkulSiparisSatiriPage.scss'

import {CaretDownOutlined, SearchOutlined} from '@ant-design/icons'
import {Button, Col, DatePicker, Input, Row, Select, Table} from 'antd'
import dayjs, {Dayjs} from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import {saveAs} from 'file-saver'
import {RangeValue} from 'rc-picker/lib/interface'
import React, {useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import {useOkulSiparisSatirlariQuery} from '../../../../gql/GeneratedGraphqlTypes'
import {getFormattedPrice} from '../../../../helpers/Formatting'
import Spinner from '../../../spinner/Spinner'
import {ReactComponent as AktarIcon} from '../okul-siparis-page/icons/aktar.svg'
import {ReactComponent as ConfirmIcon} from '../okul-siparis-page/icons/confirm.svg'
import {ReactComponent as NotConfirmIcon} from '../okul-siparis-page/icons/notConfirm.svg'
import {ReactComponent as WaitingIcon} from '../okul-siparis-page/icons/waiting.svg'

dayjs.extend(isBetween)

const {RangePicker} = DatePicker
const {Option} = Select
const {Search} = Input

const OkulSiparisSatiriPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [searchValue, setSearchValue] = useState<string>('')
    const [selectedDurum, setSelectedDurum] = useState<string | undefined>(undefined)
    const [selectedOdeme, setSelectedOdeme] = useState<string | undefined>(undefined)
    const [selectedKargo, setSelectedKargo] = useState<string | undefined>(undefined)
    const [selectedKategori, setSelectedKategori] = useState<string | undefined>(undefined)
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {okul, siparisYetki} = useContext(AppContext)
    const acilis = dayjs(okul?.okul_acilis_tarih ?? '2024-06-01')
    const bitis = dayjs('2025-06-01')
    const [dateRange, setDateRange] = useState<RangeValue<Dayjs>>(
        acilis.isBefore(bitis) ? [acilis, bitis] : [bitis, acilis]
    )

    const navigate = useNavigate()
    const {data, loading, error, refetch} = useOkulSiparisSatirlariQuery({
        variables: {
            sube_id: okul?.id,
            bas_tarih: dateRange?.[0]?.format('YYYY-MM-DD') ?? '2024-06-01',
            bit_tarih: dateRange?.[1]?.format('YYYY-MM-DD') ?? '2025-06-01',
            siparis_durumu: selectedDurum ?? null,
            odeme_tipi: selectedOdeme ?? null,
            kargo_firmasi: selectedKargo ?? null,
            kategori: parseInt(selectedKategori ? selectedKategori : '0') ?? null,
            arama: searchValue || null, // 2 karakterden küçükse arama yapılmaz
        },
        fetchPolicy: 'network-only',
    })

    // Filtreleme değiştikçe veriyi tekrar çek
    useEffect(() => {
        refetch()
    }, [searchValue, selectedDurum, selectedOdeme, selectedKargo, dateRange, refetch])

    const handleSearch = () => {
        setSearchValue(searchTerm)
    }

    if (loading) {
        return <Spinner showSpinner={loading} color="red"/>
    }

    if (error) {
        console.error(error)
        navigate('/')
        return null
    }

    const handleFilterChange = (filterType: 'durum' | 'kategori' | 'odeme' | 'kargo', value: string | undefined) => {
        if (value === null) {
            if (filterType === 'durum') setSelectedDurum(value);
            if (filterType === 'odeme') setSelectedOdeme(value);
            if (filterType === 'kategori') setSelectedKategori(value);
            if (filterType === 'kargo') setSelectedKargo(value);
        } else {
            if (filterType === 'durum') setSelectedDurum(value);
            if (filterType === 'odeme') setSelectedOdeme(value);
            if (filterType === 'kategori') setSelectedKategori(value);
            if (filterType === 'kargo') setSelectedKargo(value);
        }
    };


    const handleDateChange = (values: RangeValue<Dayjs>) => {
        setDateRange(values)

        if (values && values[0] && values[1]) {
            refetch({
                sube_id: okul?.id,
                bas_tarih: values[0].format('YYYY-MM-DD'),
                bit_tarih: values[1].format('YYYY-MM-DD'),
                siparis_durumu: selectedDurum ?? null,
                odeme_tipi: selectedOdeme ?? null,
                kargo_firmasi: selectedKargo ?? null,
                kategori: parseInt(selectedKategori ? selectedKategori : '0') ?? null,
                arama: searchValue || null,
            })
        }
    }

    const filteredData = data?.okulSiparisSatirlari?.siparis_satirlari?.filter(Boolean) ?? []
    const totalItems = filteredData.length;
    const displayedItems = selectedRowKeys.length;
    const siparisDurumOptions = [
        <Option key="tum" value={null}>Tümü</Option>, // 🔹 Tümünü ekledik
        ...(data?.okulSiparisSatirlari?.siparis_durumlari?.map((durum) => (
            <Option key={durum?.key} value={durum?.key}>
                {durum?.value}
            </Option>
        )) || [])
    ];

    const odemeTipiOptions = [
        <Option key="tum" value={null}>Tümü</Option>, // 🔹 Tümünü ekledik
        ...(data?.okulSiparisSatirlari?.odeme_tipleri?.map((odeme) => (
            <Option key={odeme?.key} value={odeme?.key}>
                {odeme?.value}
            </Option>
        )) || [])
    ];

    const kargoFirmalariOptions = [
        <Option key="tum" value={null}>Tümü</Option>, // 🔹 Tümünü ekledik
        ...(data?.okulSiparisSatirlari?.kargo_firmalari?.map((kargo) => (
            <Option key={kargo?.id} value={kargo?.id}>
                {kargo?.isim}
            </Option>
        )) || [])
    ];

    const kategoriOptions = [
        <Option key="tum" value={null}>Tümü</Option>, // 🔹 Tümünü ekledik
        ...(data?.okulSiparisSatirlari?.uyeKategorileri?.map((kategori) => (
            <Option key={kategori?.id} value={kategori?.id}>
                {kategori?.isim}
            </Option>
        )) || [])
    ];

    const columns = [
        {
            title: 'Sıra', dataIndex: 'sira', key: 'id'
        },
        {
            title: 'Sipariş', dataIndex: 'siparis_id', key: 'siparis_id',
            sorter: (a: any, b: any) => a.id - b.id,
            defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
        },
        {title: 'Müşteri', dataIndex: 'uye', key: 'uye'},
        {
            title: 'Barkod', dataIndex: 'barkod', key: 'barkod',
            sorter: (a: any, b: any) => parseInt(a.barkod) - parseInt(b.barkod)
        },
        {title: 'Ürün', dataIndex: 'urun', key: 'urun'},
        {title: 'Adet', dataIndex: 'adet', key: 'adet'},
        {title: 'Kalan Adet', dataIndex: 'kalan_adet', key: 'kalan_adet'},
        {
            title: 'Birim Fiyat',
            dataIndex: 'birim_fiyat',
            key: 'birim_fiyat',
            sorter: (a: any, b: any) => parseFloat(a.birim_fiyat) - parseFloat(b.toplam_tutar),
            render: (value: string) => getFormattedPrice(value),
        },
        {
            title: 'Durum',
            dataIndex: 'durum_aciklama',
            key: 'durum_aciklama',
            render: (_: any, record: any) => (
                <>
                    {record.durum_aciklama === 'Hazırlanıyor' ? (
                        <div className="order-status-table">
                            <WaitingIcon/> {record.durum_aciklama}{' '}
                        </div>
                    ) : record.durum_aciklama === 'Tamamlandı' ? (
                        <div className="order-status-table">
                            <ConfirmIcon/>
                            {record.durum_aciklama}
                        </div>
                    ) : record.durum_aciklama === 'İade Talebi' ? (
                        <div className="order-status-table">
                            <NotConfirmIcon/>
                            {record.durum_aciklama}
                        </div>
                    ) : (
                        <> {record.durum_aciklama} </>
                    )}
                </>
            ),
        },
        {
            title: 'Sipariş Tarihi',
            dataIndex: 'tarih',
            key: 'tarih',
            sorter: (a: any, b: any) => dayjs(a.tarih).valueOf() - dayjs(b.tarih).valueOf(),
        },
        {
            title: '',
            dataIndex: 'siparis_detay',
            key: 'siparis_detay',
            render: (_: any, record: any) => (
                <Button onClick={() => navigate(`${record.id}`)} className="okul-yonetim-admin-detail-button">
                    Detay
                </Button>
            ),
        },
    ]
    const exportAllToExcel = () => {
        const worksheetData: any[] = []

        const headers = [
            'Sipariş id',
            'Sınıf',
            'Üye adı',
            'Mail',
            'Öğrenci Adı',
            'Öğrenci Cinsiyet',
            'Tarih',
            'Ödeme Tipi',
            'Banka',
            'Ürün Barkod',
            'Ürün İsim',
            'Kdv Hariç Fiyat',
            'Kdv Tutarı',
            'Kdv Dahil Fiyat',
            'Adet',
            'Kalan Adet',
            
        ]
        worksheetData.push(headers)

        data?.okulSiparisSatirlari?.siparis_satirlari.forEach((siparisler: any) => {
            const transformedData = [
                siparisler.siparis_id || '',
                siparisler.sinif_ismi || '',
                siparisler.uye || '',
                siparisler.email|| '',
                siparisler.ogrenci_ismi || '',
                siparisler.ogrenci_cinsiyet || '',
                siparisler.tarih || '',
                siparisler.odeme_tipi_aciklama || '',
                siparisler.banka || '',
                siparisler.barkod || '',
                siparisler.urun || '',
                siparisler.kdv_haric_fiyat || '',
                siparisler.kdv_tutari || '',
                siparisler.kdv_dahil || '',
                siparisler.adet || '',
                siparisler.kalan_adet || '',
               
            ]
            worksheetData.push(transformedData)
        })

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

        const colWidths = worksheetData[0].map((_: string, colIndex: number) =>
            Math.max(...worksheetData.map((row) => (row[colIndex] ? row[colIndex].toString().length : 10))),
        )
        worksheet['!cols'] = colWidths.map((width: number) => ({wch: width}))

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
        const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})
        const today = new Date().toISOString().split('T')[0]
        const fileName = `OkulSiparisSatiri-${today}.xlsx`

        saveAs(new Blob([excelBuffer]), fileName)
    }
    return (
        <div className="okul-siparis-page">
            <div className="okul-sepeti-admin-header">
                <h1>Okul Siparişleri Satırları</h1>
                {siparisYetki &&
                    <Button onClick={exportAllToExcel} className="export-button">
                        <AktarIcon/> Dışa Aktar{' '}
                    </Button>
                }
            </div>
            {siparisYetki ?
                (
                    <>
                        <Row gutter={8} className="filter-section">
                            <Col lg={12} xl={6} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Müşteri, Sınıf veya Sipariş Ara </span>

                                <Search
                                    placeholder="Müşteri, Sınıf veya Sipariş Ara"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onSearch={handleSearch}
                                    allowClear
                                    enterButton={
                                        <div className="search-suffix-div">
                                            Ara <SearchOutlined/>{' '}
                                        </div>
                                    }
                                />
                            </Col>
                            <Col lg={12} xl={3} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Sipariş Durumu</span>
                                <Select
                                    placeholder="Sipariş Durumu"
                                    value={selectedDurum}
                                    onChange={(value) => handleFilterChange('durum', value)}
                                    allowClear
                                >
                                    {siparisDurumOptions}
                                </Select>
                            </Col>
                            <Col lg={12} xl={3} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Ödeme Tipi</span>
                                <Select
                                    placeholder="Ödeme Tipi"
                                    value={selectedOdeme}
                                    onChange={(value) => handleFilterChange('odeme', value)}
                                    allowClear
                                >
                                    {odemeTipiOptions}
                                </Select>
                            </Col>
                            <Col lg={12} xl={3} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Kargo Firması </span>
                                <Select
                                    placeholder="Kargo Firması"
                                    value={selectedKargo}
                                    onChange={(value) => handleFilterChange('kargo', value)}
                                    allowClear
                                >
                                    {kargoFirmalariOptions}
                                </Select>
                            </Col>
                            <Col lg={12} xl={3} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Kategori</span>

                                <Select
                                    placeholder="Kategori"
                                    value={selectedKategori}
                                    onChange={(value) => handleFilterChange('kategori', value)}
                                    allowClear
                                >
                                    {kategoriOptions}
                                </Select>
                            </Col>
                            <Col lg={12} xl={6} md={6} sm={12} xs={24}>
                                <span className="okul-order-page-filter-title">Tarih Aralığı</span>

                                <RangePicker
                                    allowClear
                                    suffixIcon={
                                        <>
                                            Değiştir <CaretDownOutlined/>{' '}
                                        </>
                                    }
                                    separator={false}
                                    value={dateRange}
                                    onChange={handleDateChange}
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                        </Row>

                        <Table
                            dataSource={filteredData as object[]}
                            columns={columns}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                total: totalItems,
                                showTotal: (total) => (
                                    <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                <span style={{marginLeft: '10px'}}> {/* Buton ile metin arasına boşluk koyuyoruz */}
                    Seçilen: {displayedItems} / Toplam: {total}
               </span>
                                    </div>
                                ),
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                },
                            }}
                            loading={loading}
                            rowKey="id"
                            rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
                            className="okul-yonetim-admin-table"
                            rowSelection={{
                                type: 'checkbox',
                                ...Selection,
                                onChange: (keys) => setSelectedRowKeys(keys as string[]),
                            }}
                        />
                    </>
                )
                :
                <p>Sipariş satırlarını görme yetkiniz bulunmamaktadır. İlgili okul temsilcinizden talep
                    edebilirsiniz.</p>
            }
        </div>
    )
}

export default OkulSiparisSatiriPage
